<template>
  <div>
    <!-- Form for adding new rows -->
    <v-sheet
      class="mb-0 pa-0"
      rounded
      style="background-color: transparent !important;"
    >
      <v-row align="center" no-gutters>
        <!-- Form fields container taking up most of the space -->
        <v-col>
          <v-row align="center" no-gutters>
            <v-col
              v-for="field in tableFields"
              :key="field.idx"
              :cols="field.cols || Math.floor(12 / tableFields.length)"
              class="pr-2"
            >
              <component-renderer
                :field="getClonedField(field)"
                :value="newRowFormValues[field.idx || `field-${field.label}`]"
                :show-validation="showNewRowValidation"
                :validation-error="
                  newRowValidationErrors[field.idx || `field-${field.label}`]
                "
                @input="
                  updateNewRowFormValue(
                    field.idx || `field-${field.label}`,
                    $event
                  )
                "
                @clear-validation="
                  clearNewRowValidationError(
                    field.idx || `field-${field.label}`
                  )
                "
                @validation-error="handleValidationError"
                @validation-event="handleValidationEvent"
              />
            </v-col>
          </v-row>
          <!-- Points display row
          <v-row v-if="pointsFields.length > 0" class="px-2 mt-2" no-gutters>
            <v-col>
              <div class="points-summary">
                <div
                  v-for="field in pointsFields"
                  :key="field.idx"
                  class="points-item d-inline-block"
                >
                  <v-chip
                    v-if="hasDropdownValue(field)"
                    color="primary"
                    class="mt-n3 mb-2"
                    small
                    icon
                  >
                    <v-icon left>stars</v-icon>
                    {{ getPointsValue(newRowFormValues, field) }} points
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
           -->
        </v-col>

        <!-- Buttons in auto-width column -->
        <v-col cols="auto">
          <v-row align="center" no-gutters>
            <v-btn
              text
              color="grey darken-1"
              @click="resetNewRowForm"
              class="ml-n2"
            >
              Clear
            </v-btn>
            <v-btn
              color="green"
              @click="addNewRow"
              :disabled="
                !isNewRowFormValid ||
                  Object.keys(newRowValidationErrors).length > 0
              "
              elevation="0"
              :dark="
                isNewRowFormValid &&
                  Object.keys(newRowValidationErrors).length === 0
              "
              :loading="showNewRowValidation"
              class="mr-0 ml-0"
              >Add
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <v-row no-gutters class="mt-0" justify="end">
        <v-col cols="auto">
          <v-btn small text @click="openBulkUpload" v-if="!showBulkUpload">
            Open bulk upload
          </v-btn>
          <v-btn small text @click="closeBulkUpload" v-if="showBulkUpload">
            Close bulk upload
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="showBulkUpload" no-gutters class="mt-0 mb-4">
        <v-col cols="12">
          <vue-dropzone
            id="bulk-upload-dropzone"
            ref="bulkDropzone"
            v-model="bulkUploadFile"
            :options="dropzoneOptions"
            :useCustomSlot="true"
            @vdropzone-file-added="handleBulkFileAdded"
            @vdropzone-file-removed="handleBulkFileRemoved"
            @vdropzone-error="handleBulkUploadError"
            @vdropzone-success="handleBulkUploadSuccess"
          >
            <div class="dropzone-custom-content">
              <!-- Upload icon and text -->
              <v-icon size="48" color="primary" class="mt-n5 mb-n2"
                >cloud_upload</v-icon
              >
              <div class="mb-2">
                <span class="text-dropzone">
                  Drop CSV file here or click to browse
                </span>
              </div>

              <!-- Action buttons -->
              <div class="bulk-upload-actions">
                <v-btn
                  v-if="bulkUploadFile === null"
                  small
                  color="grey darken-2"
                  @click.stop="downloadTemplate"
                  class="ma-1"
                  elevation="0"
                  dark
                >
                  <v-icon left size="16">fas fa-file-csv</v-icon>
                  Download Template
                </v-btn>
                <v-btn
                  small
                  color="green"
                  @click.stop="processFile"
                  class="ma-1"
                  v-if="bulkUploadFile !== null"
                  :loading="showProcessingFile"
                  elevation="0"
                >
                  <v-icon left size="16">autorenew</v-icon>
                  Process File
                </v-btn>
                <v-btn
                  small
                  color="grey darken-2"
                  @click.stop="openHelpDialog"
                  class="ma-1"
                  elevation="0"
                  dark
                >
                  <v-icon left size="16">help</v-icon>
                  Help
                </v-btn>
              </div>

              <!-- File info -->
              <div class="bulk-upload-info">
                Maximum file size: 5MB | File type: .csv | Max 1 file at a time
              </div>

              <!-- Show selected file info -->
              <div v-if="bulkUploadFile" class="mt-2">
                <v-chip color="primary" small>
                  <v-icon left small>description</v-icon>
                  {{ bulkUploadFile.name }}
                </v-chip>
              </div>
            </div>
          </vue-dropzone>
        </v-col>
      </v-row>

      <v-divider></v-divider>

      <v-data-table
        :headers="tableHeaders"
        :items="tableRows"
        :items-per-page="tableOptions.itemsPerPage"
        :page.sync="tableOptions.page"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, -1],
          'items-per-page-text': 'Rows per page:',
          'show-first-last-page': false,
          'show-current-page': true
        }"
        :mobile-breakpoint="0"
      >
        <!-- Display data in read-only mode or edit mode -->
        <template v-slot:item="{ item, index }">
          <tr v-if="editingIndex !== index">
            <!-- Regular display mode -->
            <td v-for="header in displayHeaders" :key="header.value">
              {{
                header.value.endsWith("_points")
                  ? getPointsValue(item, getFieldFromPointsHeader(header))
                  : formatFieldValue(item, getFieldFromHeader(header))
              }}
            </td>
            <!-- Status column -->
            <td class="text-center">
              <v-chip
                :color="getValidationStatusColor(item)"
                :text-color="getValidationStatusTextColor(item)"
                small
              >
                <v-icon small left :color="getValidationStatusTextColor(item)">
                  {{ getValidationStatusIcon(item) }}
                </v-icon>
                {{ item.validationStatus || "Valid" }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-btn
                icon
                small
                color="amber"
                @click="editRow(index)"
                class="mx-1"
              >
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="red"
                @click="removeRow(index)"
                class="mx-1"
              >
                <v-icon small>delete</v-icon>
              </v-btn>
            </td>
          </tr>
          <tr v-else>
            <!-- Inline edit mode -->
            <td v-for="header in displayHeaders" :key="header.value">
              <template v-if="!header.value.endsWith('_points')">
                <component-renderer
                  :field="getClonedField(getFieldFromHeader(header))"
                  :value="editRowFormValues[header.value]"
                  @input="updateEditRowFormValue(header.value, $event)"
                  :show-validation="showEditRowValidation"
                  :validation-error="editRowValidationErrors[header.value]"
                  @clear-validation="clearEditRowValidationError(header.value)"
                  @validation-error="handleValidationError"
                  @validation-event="handleValidationEvent"
                />
              </template>
              <template v-else>
                {{
                  getPointsValue(
                    editRowFormValues,
                    getFieldFromPointsHeader(header)
                  )
                }}
              </template>
            </td>
            <!-- Status column in edit mode -->
            <td class="text-center">
              <v-chip
                :color="getValidationStatusColor(tableRows[index])"
                :text-color="getValidationStatusTextColor(tableRows[index])"
                small
              >
                <v-icon
                  small
                  left
                  :color="getValidationStatusTextColor(tableRows[index])"
                >
                  {{ getValidationStatusIcon(tableRows[index]) }}
                </v-icon>
                {{ tableRows[index].validationStatus || "Valid" }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-btn
                icon
                small
                color="success"
                @click="saveEditedRow"
                class="mx-1"
                :disabled="
                  !isEditRowFormValid ||
                    Object.keys(editRowValidationErrors).length > 0
                "
                :loading="showEditRowValidation"
                x-small
              >
                <v-icon small>check</v-icon>
              </v-btn>
              <v-btn icon small color="grey" @click="cancelEdit" class="mx-1">
                <v-icon small>close</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>

        <!-- No data message -->
        <template v-slot:no-data>
          <div class="text-center py-2">
            No data available. Use the form above to add data.
          </div>
        </template>
      </v-data-table>

      <!-- Total Points Display -->
      <v-chip v-if="hasPointsColumns" color="primary" class="mt-n16 ml-0 mb-0">
        <v-icon left>stars</v-icon>
        Total Points: {{ calculateTotalPoints() }}
      </v-chip>

      <div v-if="showTableValidationError" class="required-label">
        *{{ field.label || "Table" }} requires at least one entry
      </div>
    </v-sheet>
    <help-dialog
      :title="helpDialog.title"
      :content="helpDialog.content"
      :show="helpDialog.show"
      :component="component"
      @close="closeHelpDialog"
    />
  </div>
</template>

<script>
import ComponentRenderer from "./ComponentRenderer.vue";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import HelpDialog from "@/components/Design/View/ViewComponents/Shared/HelpDialog.vue";

export default {
  components: {
    ComponentRenderer,
    vueDropzone: vue2Dropzone,
    HelpDialog
  },
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: Array,
      default: () => [{}]
    },
    hasAttemptedSubmit: {
      type: Boolean,
      default: false
    },
    component: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      tableOptions: {
        page: 1,
        itemsPerPage: 5,
        sortBy: [],
        sortDesc: []
      },
      helpDialog: {
        title: "Help",
        content: "Help content goes here",
        show: false
      },
      editingIndex: -1, // -1 means not editing, >= 0 means editing existing row
      newRowFormValues: {}, // Values for the add new row form
      editRowFormValues: {}, // Values for the inline edit form
      presetRecordsCache: {}, // Cache for preset records by group ID
      showNewRowValidation: false, // Validation state for new row form
      showEditRowValidation: false, // Validation state for edit row form
      // Separate validation states for row editing and new row form
      editRowValidationErrors: {}, // Stores validation errors for the row being edited
      newRowValidationErrors: {}, // Stores validation errors for the new row form
      localValidationError: null, // Local validation error for table
      showBulkUpload: false,
      bulkUploadFile: null,
      showProcessingFile: false,
      dropzoneOptions: {
        url: "https://httpbin.org/post", // This is a dummy URL, we'll handle uploads manually
        thumbnailWidth: 150,
        maxFilesize: 5, // 5MB limit for CSV files (better UX)
        maxFiles: 1, // Only allow one file at a time for bulk upload
        acceptedFiles: ".csv", // Only accept CSV files
        addRemoveLinks: false, // Disable default remove links
        dictFileTooBig:
          "File is too big ({{filesize}}MB). Max filesize: {{maxfilesize}}MB.",
        dictInvalidFileType: "Please upload a CSV file only.",
        dictResponseError: "Server responded with {{statusCode}} code.",
        dictCancelUpload: "Cancel upload",
        dictUploadCanceled: "Upload canceled.",
        dictRemoveFile: "Remove file",
        dictMaxFilesExceeded: "Only one file allowed at a time.",
        createImageThumbnails: false, // Disable automatic thumbnails
        autoProcessQueue: false, // Disable auto-processing - we handle this manually
        autoQueue: false, // Disable automatic queueing
        previewsContainer: false, // Disable default previews container
        disablePreviews: true, // Disable default previews
        previewTemplate: "<div></div>", // Empty preview template
        parallelUploads: 1, // Process one file at a time for better control
        chunking: false, // Disable chunking for better file handling
        forceChunking: false, // Never force chunking
        uploadMultiple: false, // Handle one file at a time for better control

        // File handling options
        renameFile: file => {
          // Don't rename files, preserve original names
          return file.name;
        },
        transformFile: null, // Disable file transformation to preserve data

        // CSV-specific options
        timeout: 60000 // 1 minute timeout for CSV processing
      }
    };
  },
  computed: {
    tableFields() {
      return (this.field.componentFields || []).filter(field => field.active);
    },

    tableHeaders() {
      const headers = [];

      this.tableFields.forEach(field => {
        const fieldId = field.idx || `field-${field.label}`;
        // Add the regular field header
        const header = {
          text: field.label || "Column",
          value: fieldId,
          sortable: false,
          align: "start",
          width: field.cols ? `${field.cols * 8}%` : "auto",
          fieldType: field.type, // Store field type for reference
          isPointsEnabled: this.isPointsEnabledField(field) // Store points capability
        };
        headers.push(header);

        // Check if this is a dropdown field with points capability
        if (this.isPointsEnabledField(field)) {
          const pointsHeader = {
            text: "Points",
            value: `${fieldId}_points`,
            sortable: false,
            align: "right",
            parentField: fieldId, // Store reference to parent field
            isPointsColumn: true
          };
          headers.push(pointsHeader);
        }
      });

      // Add status column (before actions)
      headers.push({
        text: "Status",
        value: "validationStatus",
        sortable: false,
        align: "center",
        width: "100px"
      });

      // Add actions column
      headers.push({
        text: "Actions",
        value: "actions",
        sortable: false,
        align: "center",
        width: "120px"
      });

      return headers;
    },

    tableRows: {
      get() {
        // Only return non-empty rows or an empty array
        return this.value && this.value.length > 0
          ? this.value.filter(row => {
              // Check if the row has any non-empty values
              return Object.values(row).some(
                val => val !== undefined && val !== null && val !== ""
              );
            })
          : [];
      },
      set(newValue) {
        this.$emit("input", newValue);
      }
    },

    isNewRowFormValid() {
      return this.isFormValid(this.newRowFormValues);
    },

    isEditRowFormValid() {
      return this.isFormValid(this.editRowFormValues);
    },

    pointsFields() {
      return this.tableFields.filter(
        field =>
          field.type === "dropdown" &&
          field.presets &&
          field.presets.presetsType === "dynamic" &&
          field.presets.valuesToDisplay &&
          Array.isArray(field.presets.valuesToDisplay) &&
          field.presets.valuesToDisplay.includes("points")
      );
    },

    formFields() {
      const fields = [];
      this.tableFields.forEach(field => {
        // Add the main field
        fields.push({
          id: field.idx || `field-${field.label}`,
          type: "field",
          data: field,
          cols: field.cols || Math.floor(12 / this.tableFields.length)
        });

        // Add points column if needed
        if (this.shouldShowPoints(field)) {
          fields.push({
            id: `${field.idx || `field-${field.label}`}-points`,
            type: "points",
            data: field,
            cols: "auto"
          });
        }
      });
      return fields;
    },

    displayHeaders() {
      return this.tableHeaders.filter(
        header =>
          header.value !== "actions" && header.value !== "validationStatus"
      );
    },

    hasPointsColumns() {
      return this.tableFields.some(field => this.isPointsEnabledField(field));
    },

    showTableValidationError() {
      return this.hasAttemptedSubmit && this.isRequired && !this.hasValidRows;
    },

    isRequired() {
      return this.field && this.field.required;
    },

    hasValidRows() {
      return (
        this.tableRows &&
        this.tableRows.length > 0 &&
        this.tableRows.some(row => {
          // Check if row has at least one non-empty value
          return Object.values(row).some(
            val => val !== undefined && val !== null && val !== ""
          );
        })
      );
    }
  },
  watch: {
    value: {
      handler(newValue) {
        // When the value prop changes from outside, reinitialize the table data
        if (newValue && Array.isArray(newValue)) {
          this.initializeTableData();
        }
      },
      deep: true
    },
    newRowFormValues: {
      deep: true,
      handler() {}
    },
    editRowFormValues: {
      deep: true,
      handler() {}
    },
    hasAttemptedSubmit: {
      handler(newVal) {
        if (newVal) {
          this.validateTable();
        }
      },
      immediate: true
    },
    tableRows: {
      handler() {
        this.validateTable();
      },
      deep: true
    }
  },
  created() {
    this.resetNewRowForm();
    this.initializeTableData();
    this.loadAllPresetRecords();
  },
  methods: {
    processFile() {
      console.log("Processing file...");
      this.showProcessingFile = true;
      this.bulkUploadFile = null;
    },
    openBulkUpload() {
      this.showBulkUpload = true;
    },
    openHelpDialog() {
      // Generate dynamic content based on actual table fields
      const activeFields = this.tableFields.filter(
        field => field.active !== false
      );
      const requiredFields = activeFields.filter(field => field.required);
      const fieldLabels = activeFields.map(field => field.label || "Column");

      // Generate column descriptions
      const columnDescriptions = activeFields
        .map(field => {
          const label = field.label || "Column";
          const description =
            field.placeholder ||
            `Enter the ${label.toLowerCase()} for each item`;
          return `<li><strong>${label}</strong>: ${description}</li>`;
        })
        .join("\n                    ");

      // Generate example CSV data
      const csvHeaders = fieldLabels.join(",");
      const exampleRows = [
        activeFields
          .map(field => {
            // Generate example data based on field type
            switch (field.type) {
              case "textfield":
                return field.label?.toLowerCase().includes("serial")
                  ? "SN001234567"
                  : field.label?.toLowerCase().includes("product")
                  ? "ABC123"
                  : field.label?.toLowerCase().includes("code")
                  ? "XYZ789"
                  : "Example123";
              case "number":
                return "10";
              case "dropdown":
                return "Option1";
              case "date":
                return "2024-01-15";
              default:
                return "Example";
            }
          })
          .join(","),
        activeFields
          .map(field => {
            switch (field.type) {
              case "textfield":
                return field.label?.toLowerCase().includes("serial")
                  ? "SN987654321"
                  : field.label?.toLowerCase().includes("product")
                  ? "DEF456"
                  : field.label?.toLowerCase().includes("code")
                  ? "PQR789"
                  : "Example456";
              case "number":
                return "25";
              case "dropdown":
                return "Option2";
              case "date":
                return "2024-01-16";
              default:
                return "Example";
            }
          })
          .join(","),
        activeFields
          .map(field => {
            switch (field.type) {
              case "textfield":
                return field.label?.toLowerCase().includes("serial")
                  ? "SN456789123"
                  : field.label?.toLowerCase().includes("product")
                  ? "GHI789"
                  : field.label?.toLowerCase().includes("code")
                  ? "STU123"
                  : "Example789";
              case "number":
                return "5";
              case "dropdown":
                return "Option3";
              case "date":
                return "2024-01-17";
              default:
                return "Example";
            }
          })
          .join(",")
      ];

      // Generate required fields list
      const requiredFieldsList =
        requiredFields.length > 0
          ? `<li>Required columns: ${requiredFields
              .map(f => f.label)
              .join(", ")}</li>`
          : "<li>All columns are optional</li>";

      // Generate troubleshooting column list
      const columnHeadersList = fieldLabels
        .map(label => `"${label}"`)
        .join(", ");

      // Show help dialog for bulk upload instructions
      this.helpDialog.title = "Bulk Upload Help";
      this.helpDialog.content = `
        <h3>What is Bulk Upload?</h3>
        <p>Bulk upload allows you to submit multiple claims at once by uploading a CSV file. This is much faster than entering each claim individually through the form.</p>
        <h4>Before You Start</h4>
        <p><strong>File Requirements:</strong></p>
        <ul>
            <li>File format: CSV only</li>
            <li>Maximum file size: 5MB</li>
            ${requiredFieldsList}
        </ul>
        <h4>Step-by-Step Instructions</h4>
        <h3>Step 1: Get the Template</h3>
        <ol>
            <li>Click the <strong>"Download Template"</strong> button</li>
            <li>Save the CSV template file to your computer</li>
            <li>Open the template in Excel, Google Sheets, or any spreadsheet program</li>
        </ol>
        <h3>Step 2: Fill in Your Data</h3>
        <ol>
            <li><strong>Keep the header row</strong> (first row) exactly as it appears in the template</li>
            <li><strong>Add your claims data</strong> starting from row 2:
                <ul>
                    ${columnDescriptions}
                </ul>
            </li>
            <li><strong>Save the file</strong> as CSV format when you're done</li>
        </ol>
        <h3>Step 3: Upload Your File</h3>
        <ol>
            <li>Click <strong>"Choose File"</strong> or <strong>"Browse"</strong> to select your completed CSV file</li>
            <li>Click <strong>"Process File"</strong> to upload and submit your claims</li>
            <li>Wait for confirmation that your file has been processed successfully</li>
        </ol>
        <h4>Example Format</h4>
        <p>Your CSV file should look like this:</p>
        <pre><code>${csvHeaders}
${exampleRows.join("\n")}</code></pre>
        <h4>Troubleshooting</h4>
        <p><strong>File won't upload?</strong></p>
        <ul>
            <li>Check that your file is under 5MB</li>
            <li>Make sure you saved it as a .csv file (not .xlsx or .xls)</li>
            <li>Verify that the column headers match exactly: ${columnHeadersList}</li>
        </ul>
        <p><strong>Missing data errors?</strong></p>
        <ul>
            <li>Ensure every row has data for all required columns${
              requiredFields.length > 0
                ? ": " + requiredFields.map(f => f.label).join(", ")
                : ""
            }</li>
            <li>Check for empty cells or rows</li>
        </ul>
      `;
      this.helpDialog.show = true;
    },
    closeHelpDialog() {
      this.helpDialog.show = false;
    },
    closeBulkUpload() {
      this.showBulkUpload = false;
    },
    handleBulkFileAdded(file) {
      // Store the file for processing
      this.bulkUploadFile = file;

      // Validate file type
      if (!file.name.toLowerCase().endsWith(".csv")) {
        this.$store.commit("setSnackbar", "Please upload a CSV file only");
        if (this.$refs.bulkDropzone) {
          this.$refs.bulkDropzone.removeFile(file);
        }
        this.bulkUploadFile = null;
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        this.$store.commit("setSnackbar", "File size exceeds 5MB limit");
        if (this.$refs.bulkDropzone) {
          this.$refs.bulkDropzone.removeFile(file);
        }
        this.bulkUploadFile = null;
        return;
      }

      this.$store.commit(
        "setSnackbar",
        `File "${file.name}" ready. Click "Process File" to start processing.`
      );
    },

    handleBulkFileRemoved(file) {
      this.bulkUploadFile = null;
      this.showProcessingFile = false;
    },

    handleBulkUploadSuccess(file) {
      this.$store.commit("setSnackbar", "Bulk upload completed successfully");
      this.bulkUploadFile = null;
      this.showBulkUpload = false;
    },

    handleBulkUploadError(file, message) {
      console.error("Bulk upload error:", message);
      this.$store.commit("setSnackbar", `Upload failed: ${message}`);
      this.bulkUploadFile = null;
    },
    downloadTemplate() {
      try {
        // Get the opportunity title from the component prop
        const opportunityTitle =
          this.component?.dialog?.opportunityTitle?.value ||
          this.$store.state.offer?.currentOffer?.opportunity?.title ||
          "BulkUpload";

        // Create timestamp in shortened format (YYYYMMDD_HHMM)
        const now = new Date();
        const timestamp = `${now.getFullYear()}${String(
          now.getMonth() + 1
        ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(
          now.getHours()
        ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;

        // Clean opportunity title for filename (remove special characters)
        const cleanTitle = opportunityTitle
          .replace(/[^a-zA-Z0-9\s]/g, "")
          .replace(/\s+/g, "_");

        // Create filename
        const filename = `${cleanTitle}_Template_${timestamp}.csv`;

        // Generate CSV header row from table fields
        const headers = this.tableFields
          .filter(field => field.active !== false) // Only include active fields
          .map(field => field.label || "Column")
          .join(",");

        // Create CSV content (just headers for template)
        const csvContent = headers + "\n";

        // Create blob and download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;"
        });
        const link = document.createElement("a");

        if (link.download !== undefined) {
          // Create object URL and trigger download
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", filename);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up object URL
          URL.revokeObjectURL(url);

          // Show success message
          this.$store.commit("setSnackbar", `Template downloaded: ${filename}`);
        } else {
          throw new Error("Download not supported in this browser");
        }
      } catch (error) {
        console.error("Error downloading template:", error);
        this.$store.commit(
          "setSnackbar",
          "Failed to download template. Please try again."
        );
      }
    },
    async processFile() {
      if (!this.bulkUploadFile) {
        this.$store.commit("setSnackbar", "No file selected for processing");
        return;
      }

      this.showProcessingFile = true;

      try {
        // Read the CSV file
        const csvText = await this.readFileAsText(this.bulkUploadFile);

        // Parse CSV content
        const rows = this.parseCSV(csvText);

        if (rows.length < 2) {
          throw new Error(
            "CSV file must have at least a header row and one data row"
          );
        }

        // Extract headers and data rows
        const headers = rows[0].map(header => header.trim());
        const dataRows = rows.slice(1).filter(
          row => row.some(cell => cell && cell.trim() !== "") // Filter out empty rows
        );

        if (dataRows.length === 0) {
          throw new Error("No data rows found in CSV file");
        }

        // Map CSV headers to table fields
        const fieldMapping = this.createFieldMapping(headers);

        // Validate that we can map the CSV headers to table fields
        const unmappedHeaders = headers.filter(header => !fieldMapping[header]);
        if (unmappedHeaders.length > 0) {
          console.warn("Unmapped CSV headers:", unmappedHeaders);
        }

        // Process each data row - load all regardless of validation
        let validCount = 0;
        let invalidCount = 0;
        const loadedRows = [];

        for (let i = 0; i < dataRows.length; i++) {
          const row = dataRows[i];

          try {
            // Create row data object matching table field structure
            const rowData = this.createRowDataFromCSV(
              row,
              headers,
              fieldMapping
            );

            // Validate the row and set status
            const validation = await this.updateRowValidationStatus(rowData);

            // Add row to table regardless of validation status
            loadedRows.push(rowData);

            if (validation.isValid) {
              validCount++;
            } else {
              invalidCount++;
            }
          } catch (rowError) {
            console.error(`Error processing row ${i + 2}:`, rowError);
            // Even on error, create a basic row with error status
            const errorRowData = {};
            this.tableFields.forEach(field => {
              const fieldId = field.idx || `field-${field.label}`;
              errorRowData[fieldId] =
                row[headers.findIndex(h => h === field.label)] || "";
            });
            this.$set(errorRowData, "validationStatus", "Invalid");
            this.$set(errorRowData, "validationErrors", [
              `Processing error: ${rowError.message}`
            ]);
            loadedRows.push(errorRowData);
            invalidCount++;
          }
        }

        // Add all rows to the table at once
        if (loadedRows.length > 0) {
          const currentRows = Array.isArray(this.value) ? [...this.value] : [];
          const newRows = [...currentRows, ...loadedRows];
          this.$emit("input", newRows);

          // Re-validate all rows to catch any duplicates across all rows
          this.$nextTick(async () => {
            await this.revalidateAllRows();
            this.validateTable();
            // Force validation check immediately after processing
            this.$nextTick(() => {
              this.validateTable();
            });
          });
        }

        // Show results summary
        const totalLoaded = loadedRows.length;
        let message = `Loaded ${totalLoaded} record(s): ${validCount} valid`;
        if (invalidCount > 0) {
          message += `, ${invalidCount} invalid (check status column)`;
        }
        this.$store.commit("setSnackbar", message);

        // Clear the file after processing
        this.bulkUploadFile = null;
        if (this.$refs.bulkDropzone && this.$refs.bulkDropzone.removeAllFiles) {
          this.$refs.bulkDropzone.removeAllFiles();
        }

        // Close the bulk upload view
        this.showBulkUpload = false;
      } catch (error) {
        console.error("Error processing CSV file:", error);
        this.$store.commit(
          "setSnackbar",
          `Processing failed: ${error.message}`
        );

        // Close the bulk upload view on error too
        this.showBulkUpload = false;
      } finally {
        this.showProcessingFile = false;
      }
    },

    // Helper method to read file as text
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = e => reject(new Error("Failed to read file"));
        reader.readAsText(file);
      });
    },

    // Helper method to parse CSV content
    parseCSV(csvText) {
      const rows = [];
      const lines = csvText.split("\n");

      for (let line of lines) {
        if (line.trim() === "") continue; // Skip empty lines

        // Simple CSV parsing (handles basic cases, can be enhanced for quoted fields)
        const row = line.split(",").map(cell => cell.trim());
        rows.push(row);
      }

      return rows;
    },

    // Helper method to create field mapping from CSV headers to table fields
    createFieldMapping(csvHeaders) {
      const mapping = {};

      csvHeaders.forEach(csvHeader => {
        // Find matching table field by label (case-insensitive)
        const matchingField = this.tableFields.find(
          field =>
            field.label && field.label.toLowerCase() === csvHeader.toLowerCase()
        );

        if (matchingField) {
          mapping[csvHeader] = {
            field: matchingField,
            fieldId: matchingField.idx || `field-${matchingField.label}`
          };
        }
      });

      return mapping;
    },

    // Helper method to create row data from CSV row
    createRowDataFromCSV(csvRow, headers, fieldMapping) {
      const rowData = {};

      headers.forEach((header, index) => {
        const mapping = fieldMapping[header];
        if (mapping && csvRow[index] !== undefined) {
          const value = csvRow[index].trim();

          // Convert value based on field type
          let convertedValue = value;
          if (mapping.field.type === "number" && value !== "") {
            convertedValue = parseFloat(value) || 0;
          } else if (mapping.field.type === "checkbox") {
            convertedValue =
              value.toLowerCase() === "true" ||
              value === "1" ||
              value.toLowerCase() === "yes";
          }

          rowData[mapping.fieldId] = convertedValue;
        }
      });

      // Add points data for dropdown fields (same as manual entry)
      this.pointsFields.forEach(field => {
        const fieldId = field.idx || `field-${field.label}`;
        const value = rowData[fieldId];
        const pointsKey = `${fieldId}_points`;

        if (value) {
          const selectedItem = this.findSelectedDynamicItem(field, value);
          if (selectedItem && selectedItem.points !== undefined) {
            rowData[pointsKey] = selectedItem.points;
            // Also store metadata about the points
            rowData[`${fieldId}_points_meta`] = {
              source: "dynamic_preset",
              originalValue: selectedItem.points,
              timestamp: new Date().toISOString(),
              importedViaBulkUpload: true
            };
          }
        }
      });

      return rowData;
    },

    // Helper method to validate row data (reuses existing validation logic)
    async validateRowData(rowData, rowNumber) {
      try {
        // Check required fields
        for (const field of this.tableFields) {
          const fieldId = field.idx || `field-${field.label}`;
          const value = rowData[fieldId];

          if (field.required && !value && value !== false) {
            return {
              isValid: false,
              error: `${field.label} is required`
            };
          }

          // Check for duplicates (same logic as manual entry)
          if (
            value !== undefined &&
            value !== null &&
            value !== "" &&
            field.type === "textfield" &&
            field.validation &&
            (field.validation.validationFormDataType === "static" ||
              field.validation.validationFormDataType === "dynamic")
          ) {
            const isDuplicate = this.tableRows.some(row => {
              const existingValue = row[fieldId];
              return (
                existingValue &&
                existingValue.toString().toLowerCase() ===
                  value.toString().toLowerCase()
              );
            });

            if (isDuplicate) {
              return {
                isValid: false,
                error: `${field.label} "${value}" already exists in table`
              };
            }
          }

          // Validate against validation rules (reuse existing logic)
          if (value !== undefined && value !== null && value !== "") {
            if (
              field.type === "textfield" &&
              field.validation?.validationFormDataType === "dynamic" &&
              field.validation?.selectedFormValidationDataSet
            ) {
              try {
                const validationResult = await this.$store.dispatch(
                  "validateTextFieldValue",
                  {
                    dataSetId: field.validation.selectedFormValidationDataSet,
                    value: value,
                    maxClaims: field.validation.maxClaims || null,
                    fieldLabel: field.label || "Field"
                  }
                );

                if (!validationResult.isValid) {
                  return {
                    isValid: false,
                    error:
                      validationResult.error || `${field.label} is not valid`
                  };
                }

                if (validationResult.claimsExceeded) {
                  const claimLimit = validationResult.claimsLimit || 1;
                  return {
                    isValid: false,
                    error:
                      claimLimit === 1
                        ? `${field.label} has already been claimed`
                        : `${field.label} has reached the claim limit of ${claimLimit}`
                  };
                }
              } catch (error) {
                return {
                  isValid: false,
                  error: `Error validating ${field.label}: ${error.message}`
                };
              }
            }
          }
        }

        return { isValid: true };
      } catch (error) {
        return {
          isValid: false,
          error: error.message
        };
      }
    },

    // Helper method to add validated row to table
    async addValidatedRow(rowData) {
      // Add points data for dropdown fields (same as manual entry)
      this.pointsFields.forEach(field => {
        const fieldId = field.idx || `field-${field.label}`;
        const value = rowData[fieldId];
        const pointsKey = `${fieldId}_points`;

        if (value) {
          const selectedItem = this.findSelectedDynamicItem(field, value);
          if (selectedItem && selectedItem.points !== undefined) {
            rowData[pointsKey] = selectedItem.points;
            rowData[`${fieldId}_points_meta`] = {
              source: "dynamic_preset",
              originalValue: selectedItem.points,
              timestamp: new Date().toISOString()
            };
          }
        }
      });

      // Add new row to table data
      const currentRows = [...this.tableRows, rowData];
      this.$emit("input", currentRows);
      this.validateTable();
    },
    // Initialize table data by removing any empty rows from the initial value
    initializeTableData() {
      if (this.value && this.value.length > 0) {
        // Filter out empty rows from the initial value
        const nonEmptyRows = this.value.filter(row => {
          return Object.values(row).some(
            val => val !== undefined && val !== null && val !== ""
          );
        });

        // If the filtered array is different from the original, update it
        if (nonEmptyRows.length !== this.value.length) {
          this.$emit("input", nonEmptyRows);
        }
      }
    },

    // Generic method to check if a form is valid
    isFormValid(formValues) {
      // Get all required fields
      const requiredFields = this.tableFields.filter(field => field.required);

      // If there are no required fields, consider the form valid
      if (requiredFields.length === 0) return true;

      // Check if all required fields have values
      return requiredFields.every(field => {
        const fieldId = field.idx || `field-${field.label}`;
        const value = formValues[fieldId];
        return value !== undefined && value !== null && value !== "";
      });
    },

    // Initialize form with default values
    initializeFormValues() {
      const formValues = {};
      this.tableFields.forEach(field => {
        const fieldId = field.idx || `field-${field.label}`;

        // Set appropriate default values based on field type
        let defaultValue = "";
        switch (field.type) {
          case "checkbox":
            defaultValue = false;
            break;
          case "number":
            defaultValue = null;
            break;
          case "date":
            defaultValue = "";
            break;
          default:
            defaultValue = "";
        }

        formValues[fieldId] = defaultValue;
      });
      return formValues;
    },

    resetNewRowForm() {
      // Initialize empty form with appropriate default values
      this.newRowFormValues = this.initializeFormValues();
      this.showNewRowValidation = false;
      this.newRowValidationErrors = {}; // Clear new row validation errors
    },

    resetEditRowForm() {
      this.editRowFormValues = {};
      this.editingIndex = -1;
      this.showEditRowValidation = false;
      this.editRowValidationErrors = {}; // Clear edit row validation errors
    },

    updateNewRowFormValue(fieldId, value) {
      // Clear validation error for this field when value changes
      if (this.newRowValidationErrors[fieldId]) {
        this.$delete(this.newRowValidationErrors, fieldId);
      }
      this.$set(this.newRowFormValues, fieldId, value);
    },

    updateEditRowFormValue(fieldId, value) {
      // Clear validation error for this field when value changes
      if (this.editRowValidationErrors[fieldId]) {
        this.$delete(this.editRowValidationErrors, fieldId);
      }
      this.$set(this.editRowFormValues, fieldId, value);

      // Update the validation status of the row being edited
      this.updateEditingRowValidationStatus();
    },

    async addNewRow() {
      this.showNewRowValidation = true;
      this.newRowValidationErrors = {}; // Reset validation errors

      if (!this.isNewRowFormValid) {
        // If form isn't valid, just show validation and return
        return;
      }

      try {
        // Create a new row with the form values
        const newRow = { ...this.newRowFormValues };

        // Validate fields using ComponentRenderer's validateFieldValue method for textfields
        // or existing logic for other field types
        for (const field of this.tableFields) {
          const fieldId = field.idx || `field-${field.label}`;
          const value = newRow[fieldId];

          // Required field validation
          if (field.required && !value && value !== false) {
            // Store the validation error
            this.$set(
              this.newRowValidationErrors,
              fieldId,
              `${field.label} is required`
            );
            this.$store.commit("setSnackbar", `${field.label} is required`);
            return;
          }

          // Check for duplicates within the current table (only for textfields with validation)
          if (
            value !== undefined &&
            value !== null &&
            value !== "" &&
            field.type === "textfield" &&
            field.validation &&
            (field.validation.validationFormDataType === "static" ||
              field.validation.validationFormDataType === "dynamic")
          ) {
            const isDuplicate = this.tableRows.some(row => {
              const existingValue = row[fieldId];
              return (
                existingValue &&
                existingValue.toString().toLowerCase() ===
                  value.toString().toLowerCase()
              );
            });

            if (isDuplicate) {
              // Store the validation error
              this.$set(
                this.newRowValidationErrors,
                fieldId,
                `${field.label} "${value}" has already been added to this table`
              );
              this.$store.commit(
                "setSnackbar",
                `${field.label} "${value}" has already been added to this table`
              );
              return;
            }
          }

          if (value !== undefined && value !== null && value !== "") {
            // For textfields with dynamic validation, use the optimized validation method
            if (
              field.type === "textfield" &&
              field.validation?.validationFormDataType === "dynamic" &&
              field.validation?.selectedFormValidationDataSet
            ) {
              try {
                const dataSetId =
                  field.validation.selectedFormValidationDataSet;

                const validationResult = await this.$store.dispatch(
                  "validateTextFieldValue",
                  {
                    dataSetId: dataSetId,
                    value: value,
                    maxClaims: field.validation.maxClaims || null,
                    fieldLabel: field.label || "Field"
                  }
                );

                if (!validationResult.isValid) {
                  // Store the validation error
                  this.$set(
                    this.newRowValidationErrors,
                    fieldId,
                    validationResult.error || `${field.label} is not valid.`
                  );
                  this.$store.commit(
                    "setSnackbar",
                    validationResult.error ||
                      `${field.label} is invalid. Please enter a valid ${field.label}`
                  );
                  return;
                }

                // Check claims limit
                if (validationResult.claimsExceeded) {
                  const claimLimit = validationResult.claimsLimit || 1;
                  const errorMsg =
                    claimLimit === 1
                      ? `${field.label} has already been claimed`
                      : `${field.label} has reached the claim limit of ${claimLimit}`;

                  this.$set(this.newRowValidationErrors, fieldId, errorMsg);
                  this.$store.commit("setSnackbar", errorMsg);
                  return;
                }
              } catch (error) {
                console.error(`Error validating field ${field.label}:`, error);
                // Store the validation error
                this.$set(
                  this.newRowValidationErrors,
                  fieldId,
                  `Error validating ${field.label}. Please enter a valid ${field.label}`
                );
                this.$store.commit(
                  "setSnackbar",
                  `Error validating ${field.label}. Please enter a valid ${field.label}`
                );
                return;
              }
            }
            // Handle static validation
            else if (
              field.validation?.validationFormDataType === "static" &&
              field.validation?.staticValidationOptions?.length > 0
            ) {
              const isValid = field.validation.staticValidationOptions.some(
                option => option.value === value
              );

              if (!isValid) {
                // Store the validation error
                this.$set(
                  this.newRowValidationErrors,
                  fieldId,
                  `${field.label} is invalid. Please enter a valid ${field.label}`
                );
                this.$store.commit(
                  "setSnackbar",
                  `${field.label} is invalid. Please enter a valid ${field.label}`
                );
                return;
              }
            }
            // Handle dynamic validation for non-textfield types (fallback to old method)
            else if (
              field.type !== "textfield" &&
              field.validation?.validationFormDataType === "dynamic" &&
              field.validation?.selectedFormValidationDataSet
            ) {
              try {
                const dataSetId =
                  field.validation.selectedFormValidationDataSet;
                let records = this.presetRecordsCache[dataSetId];

                // If records are not in cache, try to load them
                if (!records || !Array.isArray(records)) {
                  records = await this.$store.dispatch(
                    "getPresetRecords",
                    dataSetId
                  );
                  if (records && Array.isArray(records)) {
                    this.$set(this.presetRecordsCache, dataSetId, records);
                  }
                }

                if (!records || !Array.isArray(records)) {
                  // Store the validation error
                  this.$set(
                    this.newRowValidationErrors,
                    fieldId,
                    `Error: No preset records available for ${field.label}`
                  );
                  this.$store.commit(
                    "setSnackbar",
                    `Error: No preset records available for ${field.label}`
                  );
                  return;
                }

                const isValid = records.some(
                  record =>
                    record.identifier?.toLowerCase() === value?.toLowerCase()
                );
                if (!isValid) {
                  // Store the validation error
                  this.$set(
                    this.newRowValidationErrors,
                    fieldId,
                    `${field.label} is invalid. Please enter a valid ${field.label}`
                  );
                  this.$store.commit(
                    "setSnackbar",
                    `${field.label} is invalid. Please enter a valid ${field.label}`
                  );
                  return;
                }
              } catch (error) {
                console.error(`Error validating field ${field.label}:`, error);
                // Store the validation error
                this.$set(
                  this.newRowValidationErrors,
                  fieldId,
                  `Error validating ${field.label}. Please enter a valid ${field.label}`
                );
                this.$store.commit(
                  "setSnackbar",
                  `Error validating ${field.label}. Please enter a valid ${field.label}`
                );
                return;
              }
            }
          }
        }

        // Add points data for dropdown fields that support points
        this.pointsFields.forEach(field => {
          const fieldId = field.idx || `field-${field.label}`;
          const value = newRow[fieldId];
          const pointsKey = `${fieldId}_points`;

          if (value) {
            const selectedItem = this.findSelectedDynamicItem(field, value);
            if (selectedItem && selectedItem.points !== undefined) {
              newRow[pointsKey] = selectedItem.points;
              // Also store metadata about the points
              newRow[`${fieldId}_points_meta`] = {
                source: "dynamic_preset",
                originalValue: selectedItem.points,
                timestamp: new Date().toISOString()
              };
            }
          }
        });

        // Validate the new row and set status
        await this.updateRowValidationStatus(newRow);

        // Add new row to table data
        const currentRows = [...this.tableRows, newRow];
        this.$emit("input", currentRows);

        // Re-validate all rows to catch any new duplicates
        this.$nextTick(async () => {
          await this.revalidateAllRows();
          this.validateTable();
        });

        this.resetNewRowForm();
      } catch (error) {
        console.error("Error adding new row:", error);
        this.$store.commit(
          "setSnackbar",
          "Failed to add row. Please try again."
        );
      } finally {
        this.showNewRowValidation = false;
      }
    },

    editRow(index) {
      // If already editing another row, confirm first
      if (this.editingIndex !== -1 && this.editingIndex !== index) {
        if (confirm("Save your current changes first?")) {
          this.saveEditedRow();
        } else {
          this.cancelEdit();
        }
      }

      // Load row data into edit form
      this.editingIndex = index;
      const row = this.tableRows[index];

      this.editRowFormValues = {};
      this.tableFields.forEach(field => {
        const fieldId = field.idx || `field-${field.label}`;
        this.$set(this.editRowFormValues, fieldId, row[fieldId] || "");
      });

      // Also load points values
      this.pointsFields.forEach(field => {
        const fieldId = field.idx || `field-${field.label}`;
        const pointsKey = `${fieldId}_points`;
        if (row[pointsKey] !== undefined) {
          this.$set(this.editRowFormValues, pointsKey, row[pointsKey]);
        }
      });

      // Emit event when editing starts
      this.$emit("edit-state-change", {
        fieldId: this.field.idx || `field-${this.field.label}`,
        isEditing: true
      });
    },

    async saveEditedRow() {
      this.showEditRowValidation = true;
      this.editRowValidationErrors = {}; // Reset validation errors

      if (!this.isEditRowFormValid) {
        // If form isn't valid, just show validation and return
        return;
      }

      try {
        // Validate fields using optimized validation for textfields or existing logic for other field types
        for (const field of this.tableFields) {
          const fieldId = field.idx || `field-${field.label}`;
          const value = this.editRowFormValues[fieldId];

          // Required field validation
          if (field.required && !value && value !== false) {
            // Store validation error
            this.$set(
              this.editRowValidationErrors,
              fieldId,
              `${field.label} is required`
            );
            this.$store.commit("setSnackbar", `${field.label} is required`);
            return;
          }

          // Check for duplicates within the current table (excluding the row being edited, only for textfields with validation)
          if (
            value !== undefined &&
            value !== null &&
            value !== "" &&
            field.type === "textfield" &&
            field.validation &&
            (field.validation.validationFormDataType === "static" ||
              field.validation.validationFormDataType === "dynamic")
          ) {
            const isDuplicate = this.tableRows.some((row, index) => {
              // Skip the row being edited
              if (index === this.editingIndex) return false;

              const existingValue = row[fieldId];
              return (
                existingValue &&
                existingValue.toString().toLowerCase() ===
                  value.toString().toLowerCase()
              );
            });

            if (isDuplicate) {
              // Store the validation error
              this.$set(
                this.editRowValidationErrors,
                fieldId,
                `${field.label} "${value}" has already been added to this table`
              );
              // Update row validation status immediately
              this.updateEditingRowValidationStatus();
              this.$store.commit(
                "setSnackbar",
                `${field.label} "${value}" has already been added to this table`
              );
              return;
            }
          }

          if (value !== undefined && value !== null && value !== "") {
            // For textfields with dynamic validation, use the optimized validation method
            if (
              field.type === "textfield" &&
              field.validation?.validationFormDataType === "dynamic" &&
              field.validation?.selectedFormValidationDataSet
            ) {
              try {
                const dataSetId =
                  field.validation.selectedFormValidationDataSet;

                const validationResult = await this.$store.dispatch(
                  "validateTextFieldValue",
                  {
                    dataSetId: dataSetId,
                    value: value,
                    maxClaims: field.validation.maxClaims || null,
                    fieldLabel: field.label || "Field"
                  }
                );

                if (!validationResult.isValid) {
                  // Store the validation error
                  this.$set(
                    this.editRowValidationErrors,
                    fieldId,
                    validationResult.error || `${field.label} is not valid.`
                  );
                  // Update row validation status immediately
                  this.updateEditingRowValidationStatus();
                  this.$store.commit(
                    "setSnackbar",
                    validationResult.error || `${field.label} is not valid.`
                  );
                  return;
                }

                // Check claims limit
                if (validationResult.claimsExceeded) {
                  const claimLimit = validationResult.claimsLimit || 1;
                  const errorMsg =
                    claimLimit === 1
                      ? `${field.label} has already been claimed`
                      : `${field.label} has reached the claim limit of ${claimLimit}`;

                  this.$set(this.editRowValidationErrors, fieldId, errorMsg);
                  // Update row validation status immediately
                  this.updateEditingRowValidationStatus();
                  this.$store.commit("setSnackbar", errorMsg);
                  return;
                }
              } catch (error) {
                console.error(`Error validating field ${field.label}:`, error);
                // Store the validation error
                this.$set(
                  this.editRowValidationErrors,
                  fieldId,
                  `Error validating ${field.label}. Please enter a valid ${field.label}`
                );
                this.$store.commit(
                  "setSnackbar",
                  `Error validating ${field.label}. Please enter a valid ${field.label}`
                );
                return;
              }
            }
            // Handle static validation
            else if (
              field.validation?.validationFormDataType === "static" &&
              field.validation?.staticValidationOptions?.length > 0
            ) {
              const isValid = field.validation.staticValidationOptions.some(
                option => option.value === value
              );

              if (!isValid) {
                // Store validation error
                this.$set(
                  this.editRowValidationErrors,
                  fieldId,
                  `${field.label} is invalid. Please enter a valid ${field.label}`
                );
                this.$store.commit(
                  "setSnackbar",
                  `${field.label} is invalid. Please enter a valid ${field.label}`
                );
                return;
              }
            }
            // Handle dynamic validation for non-textfield types (fallback to old method)
            else if (
              field.type !== "textfield" &&
              field.validation?.validationFormDataType === "dynamic" &&
              field.validation?.selectedFormValidationDataSet
            ) {
              try {
                const dataSetId =
                  field.validation.selectedFormValidationDataSet;
                let records = this.presetRecordsCache[dataSetId];

                // If records are not in cache, try to load them
                if (!records || !Array.isArray(records)) {
                  records = await this.$store.dispatch(
                    "getPresetRecords",
                    dataSetId
                  );
                  if (records && Array.isArray(records)) {
                    this.$set(this.presetRecordsCache, dataSetId, records);
                  }
                }

                if (!records || !Array.isArray(records)) {
                  // Store validation error
                  this.$set(
                    this.editRowValidationErrors,
                    fieldId,
                    `Error: No preset records available for ${field.label}`
                  );
                  this.$store.commit(
                    "setSnackbar",
                    `Error: No preset records available for ${field.label}`
                  );
                  return;
                }

                const isValid = records.some(
                  record =>
                    record.identifier?.toLowerCase() === value?.toLowerCase()
                );
                if (!isValid) {
                  // Store validation error
                  this.$set(
                    this.editRowValidationErrors,
                    fieldId,
                    `${field.label} is invalid. Please enter a valid ${field.label}`
                  );
                  this.$store.commit(
                    "setSnackbar",
                    `${field.label} is invalid. Please enter a valid ${field.label}`
                  );
                  return;
                }
              } catch (error) {
                console.error(`Error validating field ${field.label}:`, error);
                // Store validation error
                this.$set(
                  this.editRowValidationErrors,
                  fieldId,
                  `Error validating ${field.label}. Please enter a valid ${field.label}`
                );
                this.$store.commit(
                  "setSnackbar",
                  `Error validating ${field.label}. Please enter a valid ${field.label}`
                );
                return;
              }
            }
          }
        }

        const updatedRows = [...this.tableRows];

        // Update points values before saving
        this.pointsFields.forEach(field => {
          const fieldId = field.idx || `field-${field.label}`;
          const value = this.editRowFormValues[fieldId];
          const pointsKey = `${fieldId}_points`;

          if (value) {
            const selectedItem = this.findSelectedDynamicItem(field, value);
            if (selectedItem && selectedItem.points !== undefined) {
              this.editRowFormValues[pointsKey] = selectedItem.points;
              // Also store the points metadata for edited rows
              this.editRowFormValues[`${fieldId}_points_meta`] = {
                source: "dynamic_preset",
                originalValue: selectedItem.points,
                timestamp: new Date().toISOString(),
                lastEdited: new Date().toISOString()
              };
            }
          }
        });

        // Create updated row and validate it
        const updatedRow = { ...this.editRowFormValues };
        await this.updateRowValidationStatus(updatedRow);

        // Update the row being edited
        updatedRows[this.editingIndex] = updatedRow;

        this.$emit("input", updatedRows);

        // Re-validate all rows to catch any new duplicates
        this.$nextTick(async () => {
          await this.revalidateAllRows();
          this.validateTable();
        });

        this.resetEditRowForm();
        // Emit event when editing ends
        this.$emit("edit-state-change", {
          fieldId: this.field.idx || `field-${this.field.label}`,
          isEditing: false
        });
      } catch (error) {
        console.error("Error saving edited row:", error);
        this.$store.commit(
          "setSnackbar",
          "Failed to save changes. Please try again."
        );
      } finally {
        this.showEditRowValidation = false;
      }
    },

    cancelEdit() {
      this.resetEditRowForm();
      // Emit event when editing ends
      this.$emit("edit-state-change", {
        fieldId: this.field.idx || `field-${this.field.label}`,
        isEditing: false
      });
    },

    removeRow(index) {
      if (this.tableRows.length === 0) return;

      // Confirm deletion
      if (!confirm("Are you sure you want to delete this row?")) return;

      // Get current non-empty rows
      const currentRows = [...this.tableRows];

      // Remove the row at the specified index
      currentRows.splice(index, 1);

      this.$emit("input", currentRows);
      this.validateTable();

      // If we were editing this row, cancel the edit
      if (this.editingIndex === index) {
        this.resetEditRowForm();
      } else if (this.editingIndex > index) {
        // Adjust editing index if we removed a row before the one being edited
        this.editingIndex--;
      }
    },

    formatFieldValue(row, field) {
      if (!field) {
        return "-";
      }

      const fieldId = field.idx || `field-${field.label}`;
      const value = row[fieldId];

      if (value === undefined || value === null || value === "") {
        return "-";
      }

      // Format based on field type
      switch (field.type) {
        case "checkbox":
          return value ? "Yes" : "No";
        case "date":
          return this.formatDate(value);
        case "dropdown":
          return this.formatDropdownValue(field, value);
        default:
          return value;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString();
      } catch (e) {
        return dateString;
      }
    },

    formatDropdownValue(field, value) {
      if (!value) return "-";

      // For dynamic presets with valuesToDisplay configuration
      if (field.presets && field.presets.presetsType === "dynamic") {
        // Try to find the selected item in the cached dynamic options
        const selectedItem = this.findSelectedDynamicItem(field, value);
        if (selectedItem) {
          // Ensure we're using the field's valuesToDisplay configuration
          return this.generateDisplayText(field, selectedItem);
        } else {
          // If item not found in cache, try to load it on demand
          this.loadPresetRecordsIfNeeded(field.presets.selectedPresetsGroup);
          // Return the raw value for now
          return value;
        }
      }

      // For static options
      if (field.presets && field.presets.staticOptions) {
        const option = field.presets.staticOptions.find(
          opt => (typeof opt === "object" ? opt.value : opt) === value
        );
        if (option) {
          return typeof option === "object" ? option.text : option;
        }
      }

      return value;
    },

    generateDisplayText(field, item) {
      if (!item) return "-";

      // If no valuesToDisplay configuration is provided, show identifier as fallback
      if (
        !field.presets ||
        !field.presets.valuesToDisplay ||
        field.presets.valuesToDisplay.length === 0
      ) {
        return item.identifier || "-";
      }

      const displayValues = field.presets.valuesToDisplay;

      const displayParts = displayValues
        .map(val => {
          switch (val) {
            case "identifier":
              return item.identifier;
            case "title":
              return item.title;
            case "description":
              return item.description;
            case "points":
              return item.points ? `${item.points} points` : "";
            default:
              return "";
          }
        })
        .filter(Boolean); // Remove empty values

      return displayParts.length > 0
        ? displayParts.join(" • ")
        : item.identifier || "-";
    },

    findSelectedDynamicItem(field, value) {
      if (!field || !field.presets || !field.presets.selectedPresetsGroup)
        return null;

      const groupId = field.presets.selectedPresetsGroup;
      const records = this.presetRecordsCache[groupId];

      if (records && Array.isArray(records)) {
        return records.find(item => item.identifier === value);
      }

      return null;
    },

    // Load all preset records needed for the table fields
    async loadAllPresetRecords() {
      if (!this.$store) return;

      // Find all unique preset groups used in the table
      const presetGroups = new Set();
      this.tableFields.forEach(field => {
        // CRITICAL: Skip validation data sets for textfields - they don't need the full dataset
        // Textfields with dynamic validation will use the optimized validateTextFieldValue action
        if (
          field.validation?.validationFormDataType === "dynamic" &&
          field.validation?.selectedFormValidationDataSet &&
          field.type !== "textfield"
        ) {
          presetGroups.add(field.validation.selectedFormValidationDataSet);
        }

        // Check for dropdown presets - these are needed for populating options
        if (
          field.type === "dropdown" &&
          field.presets &&
          field.presets.presetsType === "dynamic" &&
          field.presets.selectedPresetsGroup
        ) {
          presetGroups.add(field.presets.selectedPresetsGroup);
        }
      });

      // Load records for each preset group
      for (const groupId of presetGroups) {
        await this.loadPresetRecords(groupId);
      }
    },

    // Load preset records for a specific group
    async loadPresetRecords(groupId) {
      if (!this.$store || !groupId) return;

      try {
        const records = await this.$store.dispatch("getPresetRecords", groupId);
        if (records && Array.isArray(records)) {
          // Store in cache
          this.$set(this.presetRecordsCache, groupId, records);
        } else {
          console.warn(`No records found for group ${groupId}`);
        }
      } catch (error) {
        console.error(
          `Error loading preset records for group ${groupId}:`,
          error
        );
      }
    },

    // Load preset records for a specific group if not already in cache
    loadPresetRecordsIfNeeded(groupId) {
      if (!this.$store || !groupId || this.presetRecordsCache[groupId]) return;

      this.loadPresetRecords(groupId);
    },

    shouldShowPoints(field) {
      return (
        field.type === "dropdown" &&
        field.presets &&
        field.presets.presetsType === "dynamic" &&
        field.presets.valuesToDisplay &&
        Array.isArray(field.presets.valuesToDisplay) &&
        field.presets.valuesToDisplay.includes("points")
      );
    },

    getPointsValue(item, field) {
      const fieldId = field.idx || `field-${field.label}`;
      const pointsKey = `${fieldId}_points`;

      // For table display, first check if we have a stored points value
      if (item[pointsKey] !== undefined) {
        return item[pointsKey];
      }

      const value = item[fieldId];
      if (!value || !field.presets || field.presets.presetsType !== "dynamic") {
        return "0";
      }

      // Try to find the selected item in the cached dynamic options
      const selectedItem = this.findSelectedDynamicItem(field, value);
      if (selectedItem && selectedItem.points !== undefined) {
        return `${selectedItem.points}`;
      }

      return "0";
    },

    hasDropdownValue(field) {
      const fieldId = field.idx || `field-${field.label}`;
      const value = this.newRowFormValues[fieldId];
      return value !== undefined && value !== null && value !== "";
    },

    getFieldFromHeader(header) {
      // First try by field ID
      let field = this.tableFields.find(
        field => (field.idx || `field-${field.label}`) === header.value
      );

      // If not found and this is a points column, try finding by parent field
      if (!field && header.parentField) {
        field = this.tableFields.find(
          field => (field.idx || `field-${field.label}`) === header.parentField
        );
      }

      return field;
    },

    getFieldFromPointsHeader(header) {
      const baseFieldId = header.value.replace("_points", "");
      const field = this.tableFields.find(
        field => (field.idx || `field-${field.label}`) === baseFieldId
      );
      return field;
    },

    isPointsEnabledField(field) {
      return (
        field.type === "dropdown" &&
        field.presets?.presetsType === "dynamic" &&
        field.presets?.valuesToDisplay &&
        Array.isArray(field.presets.valuesToDisplay) &&
        field.presets.valuesToDisplay.includes("points")
      );
    },

    calculateTotalPoints() {
      if (!this.tableRows || !Array.isArray(this.tableRows)) return 0;

      let total = 0;
      const pointsFields = this.tableFields.filter(field =>
        this.isPointsEnabledField(field)
      );

      // Find quantity field - looking for textfield with label 'Quantity'
      const quantityField = this.tableFields.find(
        field => field.type === "textfield" && field.label === "Quantity"
      );

      this.tableRows.forEach(row => {
        pointsFields.forEach(field => {
          const fieldId = field.idx || `field-${field.label}`;
          const pointsKey = `${fieldId}_points`;
          if (row[pointsKey] !== undefined) {
            let points = parseFloat(row[pointsKey]) || 0;

            // If quantity field exists and has a value, multiply points by quantity
            if (quantityField) {
              const quantityId =
                quantityField.idx || `field-${quantityField.label}`;
              const quantityValue = row[quantityId];
              if (quantityValue) {
                points *= parseFloat(quantityValue) || 1;
              }
            }

            // Add the points to the total
            total += Math.round(points);
          }
        });
      });

      return Math.round(total);
    },

    getClonedField(field) {
      if (!field) return {};

      // Create a deep clone of the field
      const clonedField = JSON.parse(JSON.stringify(field));

      // Remove validation error property if it exists
      if (
        Object.prototype.hasOwnProperty.call(clonedField, "validationError")
      ) {
        delete clonedField.validationError;
      }

      return clonedField;
    },

    clearNewRowValidationError(fieldId) {
      if (this.newRowValidationErrors[fieldId]) {
        this.$delete(this.newRowValidationErrors, fieldId);
      }
    },

    clearEditRowValidationError(fieldId) {
      if (this.editRowValidationErrors[fieldId]) {
        this.$delete(this.editRowValidationErrors, fieldId);
        // Update row validation status when error is cleared
        this.updateEditingRowValidationStatus();
      }
    },

    validateTable() {
      const hasRequiredRows = !this.isRequired || this.hasValidRows;
      const hasNoInvalidRows = !this.hasInvalidRows();
      const isValid = hasRequiredRows && hasNoInvalidRows;
      const fieldId = this.field.idx || `field-${this.field.label}`;

      if (!hasRequiredRows && this.hasAttemptedSubmit) {
        this.localValidationError = `${this.field.label ||
          "Table"} requires at least one entry`;
        // Emit validation error to parent using the proper format
        this.$emit("validation-error", {
          fieldId: fieldId,
          error: this.localValidationError
        });
      } else if (!hasNoInvalidRows) {
        // Always emit error for invalid rows, not just when submission is attempted
        this.localValidationError = `${this.field.label ||
          "Table"} contains invalid records. Please fix or remove them.`;

        // Emit validation error to parent using the proper format
        this.$emit("validation-error", {
          fieldId: fieldId,
          error: this.localValidationError
        });
      } else {
        this.localValidationError = null;
        // Clear validation error when table becomes valid
        this.$emit("validation-error", {
          fieldId: fieldId,
          error: null
        });
      }

      // Also emit the legacy validation event for backward compatibility
      this.$emit("validation", isValid);

      return isValid;
    },

    handleValidationEvent(validationEvent) {
      // Forward the validation event to the parent component
      this.$emit("validation-event", validationEvent);
    },

    handleValidationError(errorData) {
      // Don't forward validation errors from ComponentRenderer components to the parent
      // These are internal table validation errors that should not affect the main form
      // The table's own validation is handled by the validateTable method
    },

    // Validation status methods
    getValidationStatusColor(item) {
      if (!item.validationStatus || item.validationStatus === "Valid") {
        return "success";
      } else if (item.validationStatus === "Invalid") {
        return "error";
      }
      return "grey";
    },

    getValidationStatusTextColor(item) {
      return "white";
    },

    getValidationStatusIcon(item) {
      if (!item.validationStatus || item.validationStatus === "Valid") {
        return "check_circle";
      } else if (item.validationStatus === "Invalid") {
        return "error";
      }
      return "help";
    },

    async validateRowStatus(row) {
      // Validate each field in the row
      const errors = [];

      for (const field of this.tableFields) {
        const fieldId = field.idx || `field-${field.label}`;
        const value = row[fieldId];

        // Required field validation
        if (field.required && (!value || value.toString().trim() === "")) {
          errors.push(`${field.label} is required`);
          continue;
        }

        // Skip validation if no value (for non-required fields)
        if (!value || value.toString().trim() === "") {
          continue;
        }

        // Min/Max length validation for text fields
        if (field.type === "text" && value) {
          if (field.min && value.toString().length < field.min) {
            errors.push(
              `${field.label} must be at least ${field.min} characters`
            );
          }
          if (field.max && value.toString().length > field.max) {
            errors.push(
              `${field.label} must be no more than ${field.max} characters`
            );
          }
        }

        // Number field validation
        if (
          field.type === "number" &&
          value !== null &&
          value !== undefined &&
          value !== ""
        ) {
          const numValue = parseFloat(value);
          if (isNaN(numValue)) {
            errors.push(`${field.label} must be a valid number`);
          } else {
            if (field.min !== undefined && numValue < field.min) {
              errors.push(`${field.label} must be at least ${field.min}`);
            }
            if (field.max !== undefined && numValue > field.max) {
              errors.push(`${field.label} must be no more than ${field.max}`);
            }
          }
        }

        // Database validation for textfields with dynamic validation
        if (
          field.type === "textfield" &&
          field.validation?.validationFormDataType === "dynamic" &&
          field.validation?.selectedFormValidationDataSet
        ) {
          try {
            const validationResult = await this.$store.dispatch(
              "validateTextFieldValue",
              {
                dataSetId: field.validation.selectedFormValidationDataSet,
                value: value,
                maxClaims: field.validation.maxClaims || null,
                fieldLabel: field.label || "Field"
              }
            );

            if (!validationResult.isValid) {
              errors.push(validationResult.error);
            } else if (validationResult.claimsExceeded) {
              errors.push(
                `${field.label} "${value}" has already been claimed the maximum number of times`
              );
            }
          } catch (validationError) {
            console.error(`Error validating ${field.label}:`, validationError);
            errors.push(`Error validating ${field.label}. Please try again.`);
          }
        }
      }

      // Check for duplicates within the table
      const isDuplicate = this.checkForDuplicates(row);
      if (isDuplicate) {
        errors.push("Duplicate record in table");
      }

      return {
        isValid: errors.length === 0,
        errors: errors,
        status: errors.length === 0 ? "Valid" : "Invalid"
      };
    },

    checkForDuplicates(newRow) {
      // Get all rows except the one being checked
      const otherRows = this.tableRows.filter((row, index) => row !== newRow);

      return otherRows.some(existingRow => {
        // Check if all field values match
        return this.tableFields.every(field => {
          const fieldId = field.idx || `field-${field.label}`;
          const newValue = newRow[fieldId];
          const existingValue = existingRow[fieldId];

          // Convert to strings for comparison, handling null/undefined
          const newStr = (newValue || "")
            .toString()
            .toLowerCase()
            .trim();
          const existingStr = (existingValue || "")
            .toString()
            .toLowerCase()
            .trim();

          return newStr === existingStr;
        });
      });
    },

    async updateRowValidationStatus(row) {
      const validation = await this.validateRowStatus(row);
      this.$set(row, "validationStatus", validation.status);
      this.$set(row, "validationErrors", validation.errors);
      return validation;
    },

    hasInvalidRows() {
      return this.tableRows.some(row => row.validationStatus === "Invalid");
    },

    getRowClass(item) {
      if (item.validationStatus === "Invalid") {
        return "invalid-row";
      }
      return "";
    },

    async revalidateAllRows() {
      // Re-validate all rows to catch new duplicates and database validation
      for (const row of this.tableRows) {
        await this.updateRowValidationStatus(row);
      }
    },

    updateEditingRowValidationStatus() {
      // Update validation status for the row currently being edited
      if (this.editingIndex >= 0 && this.editingIndex < this.tableRows.length) {
        const currentRow = this.tableRows[this.editingIndex];

        // Check if there are any validation errors in the edit form
        const hasValidationErrors =
          Object.keys(this.editRowValidationErrors).length > 0;

        if (hasValidationErrors) {
          // Set status to Invalid if there are validation errors
          this.$set(currentRow, "validationStatus", "Invalid");
          this.$set(
            currentRow,
            "validationErrors",
            Object.values(this.editRowValidationErrors)
          );
        } else {
          // If no validation errors, we need to do full validation of the current form values
          // Create a temporary row object with current edit values for validation
          const tempRow = { ...this.editRowFormValues };
          this.validateRowStatus(tempRow).then(validation => {
            this.$set(currentRow, "validationStatus", validation.status);
            this.$set(currentRow, "validationErrors", validation.errors);
          });
        }
      }
    }
  }
};
</script>

<style scoped>
/* Hide default dropzone styling */
#bulk-upload-dropzone .dz-preview {
  display: none !important;
}

/* Custom styling for our dropzone */
#bulk-upload-dropzone {
  min-height: 150px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  background: #f8f9fa;
  padding: 20px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

#bulk-upload-dropzone:hover {
  border-color: var(--v-primary-base);
  background-color: rgba(233, 30, 99, 0.05);
}

#bulk-upload-dropzone.dz-drag-hover {
  border-color: var(--v-primary-base);
  background-color: rgba(233, 30, 99, 0.1);
}

#bulk-upload-dropzone .dropzone-custom-content {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 4;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.text-dropzone {
  font-size: 14px;
  font-weight: 400;
  color: #666;
}

/* Disabled dropzone styling */
.disabled-dropzone {
  cursor: not-allowed !important;
  background-color: #f0f0f0 !important;
  border-color: #ccc !important;
}

.disabled-dropzone:hover {
  background-color: #f0f0f0 !important;
  border-color: #ccc !important;
}

.disabled-dropzone * {
  pointer-events: none;
}

/* Bulk upload specific styles */
.bulk-upload-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.bulk-upload-info {
  position: absolute;
  bottom: -38px;
  left: 50%;
  transform: translateX(-50%);
  color: #666;
  font-size: 12px;
  margin-top: 8px;
  text-align: center;
  white-space: nowrap;
}

/* File type and size info */
.dropzone-info-text {
  font-size: 11px;
  color: #666;
  margin-top: 4px;
  line-height: 1.3;
}
/* Make the data table and its components transparent */
::v-deep .v-data-table {
  background-color: transparent !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table {
  background-color: transparent !important;
}

::v-deep .v-data-table > .v-data-table__wrapper {
  background-color: transparent !important;
}

::v-deep .v-data-table__wrapper {
  background-color: transparent !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  background-color: transparent !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  background-color: transparent !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

::v-deep .v-data-footer {
  background-color: transparent !important;
}

.required-label {
  color: #f44336;
  font-size: 12px;
}

/* Invalid row styling */
::v-deep .invalid-row {
  background-color: rgba(244, 67, 54, 0.05) !important;
}

::v-deep .invalid-row:hover {
  background-color: rgba(244, 67, 54, 0.1) !important;
}

::v-deep .invalid-row td {
  border-left: 3px solid #f44336 !important;
}
</style>
